import { Config } from '../config.es6';
import ConvertUtils from './convert.es6';

/**
 * @classdesc アニメーション系のユーティリティクラス
 */
export default class AnimeUtils {

    /**
     * バウンドアニメーションを適用し、Animationオブジェクトを返す
     * @param {HTMLElement} $target - アニメーションさせるDOM要素
     * @param {number} rem - 初期の移動量（rem単位）
     * @param {number} [duration=1200] - アニメーションの実行時間（ミリ秒）
     * @param {number} [delay=0] - アニメーションの開始遅延（ミリ秒）
     * @returns {Animation} Web Animations APIによるAnimationオブジェクト
     */
    static getBounce($target, rem, duration = 1200, delay = 0) {
        const keyframe = [
            { offset: 0, opacity: 0, translate: `0 0 ${ConvertUtils.remToPx(-rem)}px` },
            { offset: 0.5, opacity: 1, translate: `0 0 ${ConvertUtils.remToPx(rem / 2)}px` },
            { offset: 0.6, translate: `0 0 ${ConvertUtils.remToPx(-rem / 4)}px` },
            { offset: 0.7, translate: `0 0 ${ConvertUtils.remToPx(rem / 8)}px` },
            { offset: 0.8, translate: `0 0 ${ConvertUtils.remToPx(-rem / 16)}px` },
            { offset: 0.9, translate: `0 0 ${ConvertUtils.remToPx(rem / 32)}px` },
            { offset: 1, opacity: 1, translate: 'none' },
        ];

        const option = { delay, duration, easing: 'ease', fill: 'both' };

        if ($target.classList.contains('js-sub_mv__explode1')) {
            keyframe[0].opacity = 1;
        }

        return $target.animate(keyframe, option);
    }

    /**
     * easeOutBackのアニメーションを適用し、Animationオブジェクトを返す
     * @param {HTMLElement} $target - アニメーションさせるDOM要素
     * @param {number} rem - 初期の移動量（rem単位）
     * @param {number} [duration=1200] - アニメーションの実行時間（ミリ秒）
     * @param {number} [delay=0] - アニメーションの開始遅延（ミリ秒）
     * @returns {Animation} Web Animations API によるAnimationオブジェクト
     */
    static getOutBack($target, rem, duration = 1200, delay = 0) {
        const keyframe = [
            { offset: 0, opacity: 0, translate: `0 0 ${ConvertUtils.remToPx(-rem)}px` },
            { offset: 0.5, opacity: 1, translate: `0 0 ${ConvertUtils.remToPx(rem / 2)}px` },
            { offset: 1, opacity: 1, translate: 'none' },
        ];

        const option = { delay, duration, easing: Config.EASING.easeOutBack, fill: 'both' };

        return $target.animate(keyframe, option);
    }

    /**
     * ボックスアニメーションを適用し、Animationオブジェクトの配列を返す
     * @param {HTMLElement} $target - アニメーションさせるDOM要素
     * @param {number} [duration=1200] - アニメーションの実行時間（ミリ秒）
     * @param {number} [delay=0] - アニメーションの開始遅延（ミリ秒）
     * @returns {Animation[]} Web Animations API によるAnimationオブジェクトの配列
     */
    static getBox($target, duration = 1600, delay = 0) {
        const keyframes = {
            box1: [
                { offset: 0.3, scale: '1.05 0.7' },
                { offset: 0.4, translate: '0 -10%' },
                { offset: 0.6, scale: '0.65 1', translate: '0 -45%' },
                { offset: 0.8, translate: 'none' },
                { offset: 0.9, scale: '1.2 0.92' },
                { offset: 1, scale: '1', translate: 'none' },
            ],
            box2: [
                { offset: 0.25, rotate: '-1deg' },
                { offset: 0.75, rotate: '1deg' },
            ],
        };

        const options = {
            box1: { delay, duration, easing: Config.EASING.easeInOutCirc, fill: 'both' },
            box2: { delay, duration: duration / 10, iterations: 5 },
        };

        if ($target.classList.contains('js-sub_mv__box')) {
            const obj = { offset: 0.95, opacity: 1 };
            keyframes.box1.splice(keyframes.box1.length - 1, 0, obj);
            keyframes.box1.at(-1).opacity = 0;
        }

        return [
            $target.animate(keyframes.box1, options.box1),
            $target.animate(keyframes.box2, options.box2),
        ];
    }

    /**
     * フェードアウトのアニメーションを適用し、Animationオブジェクトを返す
     * @param {HTMLElement} $target - アニメーションさせるDOM要素
     * @param {number} [duration=1200] - アニメーションの実行時間（ミリ秒）
     * @param {number} [delay=0] - アニメーションの開始遅延（ミリ秒）
     * @returns {Animation} Web Animations API によるAnimationオブジェクト
     */
    static getFadeOut($target, duration = 1200, delay = 0) {
        const keyframe = [{ opacity: 1 }, { opacity: 0 }];
        const option = { delay, duration, easing: 'ease-in', fill: 'both' };

        return $target.animate(keyframe, option);
    }
}
